import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'

export interface LightboxItem {
    kind: 'image' | 'pdf'
    src: string
    caption?: string
    downloadName?: string
}

interface LightboxProps {
    items: LightboxItem[]
    openIndex: number | null
    onClose: () => void
    onIndexChange?: (index: number) => void
}

const MIN_ZOOM = 0.5
const MAX_ZOOM = 4
const ZOOM_STEP = 0.5

export default function Lightbox({
    items,
    openIndex,
    onClose,
    onIndexChange
}: LightboxProps) {
    const isOpen = openIndex !== null && items[openIndex] !== undefined
    const current = isOpen ? items[openIndex] : null
    const [zoom, setZoom] = useState(1)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

    useEffect(() => {
        setZoom(1)
        setPan({ x: 0, y: 0 })
    }, [openIndex])

    const goPrev = useCallback(() => {
        if (openIndex === null) return
        const next = openIndex === 0 ? items.length - 1 : openIndex - 1
        onIndexChange?.(next)
    }, [openIndex, items.length, onIndexChange])

    const goNext = useCallback(() => {
        if (openIndex === null) return
        const next = openIndex === items.length - 1 ? 0 : openIndex + 1
        onIndexChange?.(next)
    }, [openIndex, items.length, onIndexChange])

    useEffect(() => {
        if (!isOpen) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
            else if (e.key === 'ArrowLeft') goPrev()
            else if (e.key === 'ArrowRight') goNext()
            else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))
            else if (e.key === '-') setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))
            else if (e.key === '0') {
                setZoom(1)
                setPan({ x: 0, y: 0 })
            }
        }
        window.addEventListener('keydown', onKey)
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            window.removeEventListener('keydown', onKey)
            document.body.style.overflow = prevOverflow
        }
    }, [isOpen, onClose, goPrev, goNext])

    if (!isOpen || !current) return null

    const downloadName =
        current.downloadName ||
        decodeURIComponent(current.src.split('/').pop() || 'download')

    const onPointerDown = (e: React.PointerEvent) => {
        if (zoom === 1) return
        setIsDragging(true)
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
        ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    }
    const onPointerMove = (e: React.PointerEvent) => {
        if (!isDragging) return
        setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y })
    }
    const onPointerUp = () => setIsDragging(false)

    const node = (
        <div
            role="dialog"
            aria-modal
            className="fixed inset-0 z-[100] flex flex-col"
            style={{
                background: 'rgba(13, 11, 8, 0.94)',
                backdropFilter: 'saturate(120%) blur(8px)'
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose()
            }}
        >
            <div
                className="flex items-center justify-between px-5 md:px-8 py-4"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
                <div className="flex items-center gap-4 min-w-0">
                    <span
                        className="text-[11px] uppercase font-semibold"
                        style={{ letterSpacing: '0.22em', color: 'rgba(243,236,217,0.5)' }}
                    >
                        {String(openIndex + 1).padStart(2, '0')} /{' '}
                        {String(items.length).padStart(2, '0')}
                    </span>
                    {current.caption && (
                        <div
                            className="font-display truncate"
                            style={{
                                fontSize: '1rem',
                                color: 'rgba(243,236,217,0.92)',
                                letterSpacing: '-0.012em'
                            }}
                        >
                            {current.caption}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {current.kind === 'image' && (
                        <>
                            <ToolbarBtn
                                title="Zoom out"
                                onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))}
                                disabled={zoom <= MIN_ZOOM}
                            >
                                <MinusIcon />
                            </ToolbarBtn>
                            <span
                                className="text-[11px] uppercase font-semibold tabular-nums"
                                style={{
                                    letterSpacing: '0.18em',
                                    color: 'rgba(243,236,217,0.65)',
                                    minWidth: '3rem',
                                    textAlign: 'center'
                                }}
                            >
                                {Math.round(zoom * 100)}%
                            </span>
                            <ToolbarBtn
                                title="Zoom in"
                                onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}
                                disabled={zoom >= MAX_ZOOM}
                            >
                                <PlusIcon />
                            </ToolbarBtn>
                            <ToolbarSep />
                        </>
                    )}
                    <ToolbarBtn
                        as="a"
                        title="Download"
                        href={current.src}
                        download={downloadName}
                    >
                        <DownloadIcon />
                    </ToolbarBtn>
                    <ToolbarBtn
                        as="a"
                        title="Open in new tab"
                        href={current.src}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <ExternalIcon />
                    </ToolbarBtn>
                    <ToolbarSep />
                    <ToolbarBtn title="Close (Esc)" onClick={onClose}>
                        <CloseIcon />
                    </ToolbarBtn>
                </div>
            </div>

            <div className="relative flex-1 flex items-center justify-center overflow-hidden">
                {items.length > 1 && (
                    <>
                        <NavButton side="left" onClick={goPrev}>
                            <ChevronLeftIcon />
                        </NavButton>
                        <NavButton side="right" onClick={goNext}>
                            <ChevronRightIcon />
                        </NavButton>
                    </>
                )}

                {current.kind === 'image' ? (
                    <div
                        className="select-none"
                        style={{
                            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            touchAction: zoom > 1 ? 'none' : 'auto'
                        }}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                        onDoubleClick={() => {
                            if (zoom === 1) setZoom(2)
                            else {
                                setZoom(1)
                                setPan({ x: 0, y: 0 })
                            }
                        }}
                    >
                        <img
                            src={current.src}
                            alt={current.caption || downloadName}
                            draggable={false}
                            style={{
                                maxWidth: '92vw',
                                maxHeight: 'calc(100vh - 12rem)',
                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                transition: isDragging ? 'none' : 'transform 0.18s ease',
                                boxShadow: '0 32px 80px -24px rgba(0,0,0,0.6)',
                                border: '1px solid rgba(255,255,255,0.08)'
                            }}
                        />
                    </div>
                ) : (
                    <iframe
                        title={current.caption || downloadName}
                        src={current.src}
                        className="bg-white"
                        style={{
                            width: '92vw',
                            height: 'calc(100vh - 12rem)',
                            maxWidth: '1100px',
                            border: '1px solid rgba(255,255,255,0.08)',
                            boxShadow: '0 32px 80px -24px rgba(0,0,0,0.6)'
                        }}
                    />
                )}
            </div>

            <div
                className="px-5 md:px-8 py-3 flex flex-wrap items-center justify-between gap-2 text-[10.5px] uppercase font-semibold"
                style={{
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    letterSpacing: '0.22em',
                    color: 'rgba(243,236,217,0.4)'
                }}
            >
                <div className="flex flex-wrap gap-4">
                    {items.length > 1 && <span>← → Navigate</span>}
                    {current.kind === 'image' && <span>+ / − Zoom</span>}
                    <span>Esc Close</span>
                </div>
                <div>{downloadName}</div>
            </div>
        </div>
    )

    return createPortal(node, document.body)
}

interface ToolbarBtnProps {
    title: string
    onClick?: () => void
    disabled?: boolean
    children: React.ReactNode
}
type AnchorBtnProps = ToolbarBtnProps & {
    as: 'a'
    href: string
    download?: string
    target?: string
    rel?: string
}
function ToolbarBtn(props: ToolbarBtnProps | AnchorBtnProps) {
    const style: React.CSSProperties = {
        width: '2.4rem',
        height: '2.4rem',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: 'rgba(243,236,217,0.85)',
        borderRadius: '2px',
        transition: 'all 0.15s ease'
    }
    const className =
        'inline-flex items-center justify-center hover:bg-white/10 hover:border-white/30 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed'

    if ('as' in props && props.as === 'a') {
        return (
            <a
                title={props.title}
                href={props.href}
                download={props.download}
                target={props.target}
                rel={props.rel}
                className={className}
                style={style}
            >
                {props.children}
            </a>
        )
    }
    return (
        <button
            type="button"
            title={props.title}
            onClick={props.onClick}
            disabled={props.disabled}
            className={className}
            style={style}
        >
            {props.children}
        </button>
    )
}

function ToolbarSep() {
    return (
        <span
            aria-hidden
            style={{
                width: '1px',
                height: '1.5rem',
                background: 'rgba(255,255,255,0.12)',
                margin: '0 0.35rem'
            }}
        />
    )
}

function NavButton({
    side,
    onClick,
    children
}: {
    side: 'left' | 'right'
    onClick: () => void
    children: React.ReactNode
}) {
    return (
        <button
            type="button"
            aria-label={side === 'left' ? 'Previous' : 'Next'}
            onClick={onClick}
            className="absolute top-1/2 -translate-y-1/2 inline-flex items-center justify-center transition-all"
            style={{
                width: '3rem',
                height: '3rem',
                background: 'rgba(0,0,0,0.45)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: 'rgba(243,236,217,0.9)',
                borderRadius: '50%',
                [side === 'left' ? 'left' : 'right']: '1.25rem'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0,0,0,0.7)'
                e.currentTarget.style.borderColor = 'var(--color-gold-500)'
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0,0,0,0.45)'
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'
            }}
        >
            {children}
        </button>
    )
}

function ChevronLeftIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
        </svg>
    )
}
function ChevronRightIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
        </svg>
    )
}
function PlusIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
        </svg>
    )
}
function MinusIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M5 12h14" />
        </svg>
    )
}
function DownloadIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
        </svg>
    )
}
function ExternalIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
        </svg>
    )
}
function CloseIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
        </svg>
    )
}
